import { useNavigate, useParams } from "react-router-dom";
import { useSubmitAnswerMutation } from "src/api/services/answersApi";
import { MatchParam } from "src/constants/matches";
import { Paths } from "src/constants/paths";
import { useQuestions } from "src/context/QuestionsProvider";
import { useVisit } from "src/context/VisitProvider";
import { useContinue } from "./useContinue";
import { useAnswer } from "src/context/AnswerProvider";
import { useCallback } from "react";
import { VisitType } from "src/api/types";
import { useCancellationContinue } from "./useCancellationContinue";
import { useMaybeCancellationModal } from "src/context/CancellationModalProvider/useMaybeCancellationModal";

interface AttachedAnswerProps {
  [key: string]: string;
}

const getNextPath = (visitType: VisitType) => {
  if (visitType === "Cancellation") {
    return;
  }

  if (visitType === "Refill") {
    return Paths.REFILL;
  }

  if (visitType === "Upsell") {
    return Paths.ADD_ONS;
  }

  return Paths.QUESTIONS;
};

export const useSubmitAnswers = () => {
  const [submitAnswer] = useSubmitAnswerMutation();
  const slug = useQuestions((q) => q.slug);
  const questionType = useQuestions((q) => q.question_type);
  const next = useVisit(
    (v) => v.questions?.find((q) => q.slug === slug)?.next_question,
  );
  const visitType = useVisit((v) => v.visit_type);
  const { questionId, visitId } =
    useParams<MatchParam<"QUESTION_ID" | "VISIT_ID" | "SUBSCRIPTION_ID">>();
  const navigateGeneric = useContinue();
  const routerNavigate = useNavigate();
  const navigateCancellation = useCancellationContinue();
  const { details, setAnswers, setDetails } = useAnswer();

  const cancellation = useMaybeCancellationModal();

  const navigate =
    visitType === "Cancellation" ? navigateCancellation : navigateGeneric;

  const nextQuestion = useCallback(
    (attachedAnswer?: AttachedAnswerProps) => {
      if (next == null) {
        if (["Initial", "Upsell"].includes(visitType)) {
          routerNavigate(Paths.PATIENT_DASHBOARD);
          return;
        }

        if (visitType === "Check-in") {
          navigate(`check-in/complete`);
          return;
        }

        if (visitType === "Refill") {
          navigate(`refill/complete`);
          return;
        }

        if (visitType === "Cancellation") {
          cancellation?.scheduleCancellation();
        }
      } else {
        const path = getNextPath(visitType);
        navigate(`${path != null ? path + "/" : ""}${next}`, {
          mergeSearch: attachedAnswer ? attachedAnswer : undefined,
          viewTransition: true,
        });
        return;
      }
    },
    [cancellation, navigate, next, routerNavigate, visitType],
  );

  const handleSubmit = useCallback(
    async (answer: string[], nextQ?: string, skipNextQuestion?: boolean) => {
      if (visitId && questionId) {
        submitAnswer({
          visitId,
          question_id: questionId,
          question_type: questionType,
          response: {
            answer: answer,
            details,
          },
        });

        setAnswers([]);
        setDetails(undefined);
      }

      if (nextQ != null) {
        const path = getNextPath(visitType);
        navigate(`${path != null ? path + "/" : ""}${nextQ}`, {
          viewTransition: true,
        });
        return;
      }

      if (!skipNextQuestion) {
        nextQuestion();
      }
    },
    [
      details,
      navigate,
      nextQuestion,
      questionId,
      questionType,
      setAnswers,
      setDetails,
      submitAnswer,
      visitId,
      visitType,
    ],
  );

  return { submitAnswer: handleSubmit, nextQuestion };
};
